import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Typography from "@mui/material/Typography";
import { CheckCircle2, XCircle } from "lucide-react";
import { useFormContext } from "react-hook-form";
import {
  CAMPAIGN_AGENT_APPLIED_BADGE,
  CAMPAIGN_AGENT_DISCARDED_BADGE,
  CAMPAIGN_AGENT_DONE_CAPTION,
  BUTTON_CAMPAIGN_AGENT_VIEW_CHANGES,
} from "../../../i18n/keysTranslations";
import { useTranslationApp } from "../../../lib/i18next";
import { INPUT_COLOR_BORDER } from "../../../utils/colors";
import { BORDER_RADIUS } from "../../../utils/constants";
import { CAMPAIGN_AGENT_OPTIMIZATION_STATUS } from "../../../utils/campaignAgentOptimizations";
import CampaignAgentChangesSummary from "./CampaignAgentChangesSummary";

const CampaignAgentOptimizationStatusCard = ({
  optimization,
  status,
  onViewChanges,
}) => {
  const { t } = useTranslationApp();
  const { getValues } = useFormContext();
  const isDiscarded = status === CAMPAIGN_AGENT_OPTIMIZATION_STATUS.DISCARDED;
  const isManual =
    Boolean(optimization?.isExternal) ||
    Boolean(
      getValues(`platformsData.${optimization?.platform}.isIncompatible`)
    );

  const captionKey =
    isManual && !isDiscarded ? CAMPAIGN_AGENT_DONE_CAPTION : null;
  const changes = Array.isArray(optimization?.changes)
    ? optimization.changes
    : [];
  const showAppliedChanges = !isDiscarded && !isManual && changes.length > 0;

  const StatusIcon = isDiscarded ? XCircle : CheckCircle2;

  return (
    <Box
      data-optimization-status={status}
      sx={{
        border: `1px solid ${INPUT_COLOR_BORDER}`,
        borderRadius: `${BORDER_RADIUS * 4}px`,
        bgcolor: "fields.background",
        p: 1.5,
        display: "flex",
        gap: 1.5,
        width: "100%",
      }}
    >
      <Box
        sx={{
          color: isDiscarded ? "text.disabled" : "success.main",
          display: "flex",
          flexShrink: 0,
        }}
      >
        <StatusIcon size={18} />
      </Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            flexWrap: "wrap",
          }}
        >
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 600,
              overflow: "hidden",
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              minWidth: 0,
              color: isDiscarded ? "text.secondary" : undefined,
            }}
          >
            {optimization?.title}
          </Typography>
          <Chip
            size="small"
            variant="outlined"
            label={t(
              isDiscarded
                ? CAMPAIGN_AGENT_DISCARDED_BADGE
                : CAMPAIGN_AGENT_APPLIED_BADGE
            )}
            sx={isDiscarded ? { color: "text.secondary" } : undefined}
          />
        </Box>
        {captionKey ? (
          <Typography variant="caption" color="text.secondary">
            {t(captionKey)}
          </Typography>
        ) : null}
        {showAppliedChanges ? (
          <Box sx={{ mt: 1 }}>
            <CampaignAgentChangesSummary
              optimization={optimization}
              showHeading={false}
              showFootnote={false}
            />
            {onViewChanges ? (
              <Typography
                component="button"
                type="button"
                variant="caption"
                color="text.secondary"
                onClick={onViewChanges}
                sx={(theme) => ({
                  ...theme.typography.caption,
                  display: "block",
                  mt: 0.5,
                  p: 0,
                  border: 0,
                  background: "none",
                  cursor: "pointer",
                  textDecoration: "underline",
                  textAlign: "left",
                })}
              >
                {t(BUTTON_CAMPAIGN_AGENT_VIEW_CHANGES)}
              </Typography>
            ) : null}
          </Box>
        ) : null}
      </Box>
    </Box>
  );
};

export default CampaignAgentOptimizationStatusCard;
